/**
 * Console board for the map budget, calibration and lookup gaps.
 *
 * Everything the engine spends quota on, and everything an operator can do about
 * it, is on one page: the counters and the standing blocks, the calibration
 * proposals the samples justify, and the departure points members searched for
 * and nothing matched. The interfaces existed before the page did, so an operator
 * could only read them as JSON.
 *
 * The page is read-mostly. The three write actions are the ones that change what
 * the engine will do next — clearing a block, applying a coefficient change,
 * pruning the gap list — and each says what it is about to do before it does it.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { AdminCalibrationRunDto, AdminProviderStatusDto } from '@shared/types'

import { ErrorAlert, ErrorBlock, LoadingBlock } from '../../components/layout/states'
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { EmptyState, Stat, StatGrid } from '../../components/ui/primitives'
import {
  useAdminCalibration,
  useAdminLookupMisses,
  useAdminProviders,
  useApplyCalibration,
  usePreviewCalibration,
  usePruneLookupMisses,
  useResetBreaker,
} from '../../hooks/use-admin'
import { formatUnixSeconds } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { mutationErrorText } from '../../lib/mutation-error'
import { AdminPage, CollapsibleRow, Pager } from './shared'

/** One usage line: a provider, a capped service, and what it has spent. */
interface UsageRow {
  provider: string
  service: string
  used: number
  limit: number
  windowKind: 'day' | 'month' | 'balance'
  updatedAt: number | null
}

/**
 * Every capped service of every configured provider.
 *
 * The counter table only has a row once a provider has been called, so this joins
 * the provider list the server reports against those rows: a service with no call
 * today is shown at zero rather than omitted, which is what makes the allowance
 * readable on a quiet day.
 */
function buildUsageRows(status: AdminProviderStatusDto | undefined): UsageRow[] {
  if (!status) {
    return []
  }
  const services: Array<UsageRow['service']> = [
    'poi_search',
    'geocode',
    'route_transit',
    'route_driving',
    'route_walking',
    'route_bicycling',
  ]
  const rows: UsageRow[] = []
  for (const entry of status.providers) {
    if (!entry.configured) {
      continue
    }
    for (const service of services) {
      const spent = status.budgets.find(
        (row) => row.provider === entry.provider && row.service === service,
      )
      rows.push({
        provider: entry.provider,
        service,
        used: spent?.used ?? 0,
        limit: spent?.limit ?? status.dailyLimitPerProvider,
        windowKind: spent?.windowKind ?? 'day',
        updatedAt: spent?.updatedAt ?? null,
      })
    }
  }
  return rows
}

/** How the budget bar is coloured: the last fifth is the warning zone. */
function usageVariant(used: number, limit: number): 'success' | 'warning' | 'destructive' {
  if (limit <= 0) {
    return 'destructive'
  }
  const ratio = used / limit
  if (ratio >= 1) {
    return 'destructive'
  }
  return ratio >= 0.8 ? 'warning' : 'success'
}

/** Colour for a breaker that is still in force. */
function breakerVariant(breaker: AdminProviderStatusDto['breakers'][number]): 'destructive' | 'warning' {
  return breaker.resetAt === null ? 'destructive' : 'warning'
}

/** Colour for a calibration proposal's decision state. */
function calibrationVariant(status: AdminCalibrationRunDto['status']): 'warning' | 'success' | 'secondary' {
  if (status === 'pending') {
    return 'warning'
  }
  return status === 'applied' ? 'success' : 'secondary'
}

export function AdminMapDataPage() {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const providers = useAdminProviders()
  const misses = useAdminLookupMisses(page)
  const calibration = useAdminCalibration()
  const resetBreaker = useResetBreaker()
  const previewCalibration = usePreviewCalibration()
  const applyCalibration = useApplyCalibration()
  const pruneMisses = usePruneLookupMisses()
  const locale = currentLocale()

  const actionError =
    resetBreaker.error ??
    previewCalibration.error ??
    applyCalibration.error ??
    pruneMisses.error
  const actionErrorText = mutationErrorText(actionError, t)

  const budget = providers.data
  const usage = buildUsageRows(budget)
  const runs = calibration.data?.runs ?? []
  const pendingRuns = runs.filter((run) => run.status === 'pending')
  const stamp = (seconds: number | null): string =>
    seconds === null ? t('admin.mapData.never') : formatUnixSeconds(seconds, locale)

  return (
    <AdminPage
      title={t('admin.mapData.title')}
      description={t('admin.mapData.description')}
      current={t('admin.nav.mapData')}
    >
      {actionErrorText ? (
        <ErrorAlert message={actionErrorText} />
      ) : null}

      {/* ---------------------------------------------------------------- */}
      {/* Usage                                                             */}
      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3">
          <CardTitle>{t('admin.mapData.usageTitle')}</CardTitle>
          {budget ? (
            <Badge variant={budget.mode === 'readonly' ? 'destructive' : 'success'}>
              {t(`admin.mapData.mode.${budget.mode}`)}
            </Badge>
          ) : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-sm">
          {providers.isPending ? <LoadingBlock label={t('common.loading')} /> : null}
          {providers.isError ? (
            <ErrorBlock
              title={t('admin.common.error')}
              retryLabel={t('admin.common.retry')}
              onRetry={() => void providers.refetch()}
            />
          ) : null}

          {budget ? (
            <>
              <p className="text-muted-foreground text-xs">
                {t('admin.mapData.dailyLimit', { limit: budget.dailyLimitPerProvider })}
                {budget.mode === 'readonly' ? ` · ${t('admin.mapData.readonlyHint')}` : ''}
              </p>

              {budget.providers.filter((entry) => entry.configured).length === 0 ? (
                <p className="text-muted-foreground">{t('admin.mapData.usageNoProvider')}</p>
              ) : null}

              {/* The vendor meters each capability separately, so the board lists
                  every capped service of every configured provider. A provider
                  with no call today has no row in the counter table, and showing
                  nothing for it would read as "no allowance" rather than "none
                  spent". */}
              {usage.map((row) => (
                <div
                  key={`${row.provider}:${row.service}`}
                  className="flex flex-wrap items-center gap-2"
                >
                  <Badge variant={usageVariant(row.used, row.limit)}>
                    {row.used} / {row.limit}
                  </Badge>
                  <span className="font-medium">{row.provider}</span>
                  <span className="text-muted-foreground">
                    {t(`admin.mapData.service.${row.service}`)}
                  </span>
                  {row.updatedAt === null ? (
                    <span className="text-muted-foreground text-xs">
                      {t('admin.mapData.usageUntouched')}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-xs">
                      {t(`admin.mapData.window.${row.windowKind}`)}
                      {' · '}
                      {stamp(row.updatedAt)}
                    </span>
                  )}
                </div>
              ))}
            </>
          ) : null}
        </CardContent>
      </Card>

      {/* ---------------------------------------------------------------- */}
      {/* Circuit breakers                                                  */}
      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>{t('admin.mapData.breakersTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          <p className="text-muted-foreground text-xs">{t('admin.mapData.breakersHint')}</p>
          {budget && budget.breakers.length === 0 ? (
            <p className="text-muted-foreground">{t('admin.mapData.breakersEmpty')}</p>
          ) : null}
          {budget?.breakers.map((breaker) => (
            <div
              key={`${breaker.provider}:${breaker.service}`}
              className="flex flex-wrap items-center gap-2"
            >
              <Badge variant={breakerVariant(breaker)}>
                {t(`admin.mapData.service.${breaker.service}`)}
              </Badge>
              <span className="font-medium">{breaker.provider}</span>
              <span className="text-muted-foreground min-w-0 grow truncate text-xs">
                {breaker.reason}
              </span>
              <span className="text-muted-foreground text-xs">
                {breaker.resetAt === null
                  ? t('admin.mapData.breakerManual')
                  : t('admin.mapData.breakerUntil', { date: stamp(breaker.resetAt) })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={resetBreaker.isPending}
                onClick={() =>
                  resetBreaker.mutate({ provider: breaker.provider, service: breaker.service })
                }
              >
                {t('admin.mapData.resetBreaker')}
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ---------------------------------------------------------------- */}
      {/* Calibration                                                       */}
      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3">
          <CardTitle>{t('admin.mapData.calibrationTitle')}</CardTitle>
          <Button
            variant="outline"
            size="sm"
            disabled={previewCalibration.isPending}
            onClick={() => previewCalibration.mutate()}
          >
            {previewCalibration.isPending
              ? t('admin.mapData.calibrationFitting')
              : t('admin.mapData.calibrationPreview')}
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          <p className="text-muted-foreground text-xs">{t('admin.mapData.calibrationHint')}</p>
          <p className="text-muted-foreground text-xs">{t('admin.mapData.calibrationSampleHint')}</p>

          {/* The proposal count is stated before the list, because applying one is
              a change to every future estimate and the operator should see how
              many questions are waiting. */}
          <StatGrid className="grid-cols-2">
            <Stat label={t('admin.mapData.calibrationProposals')} value={runs.length} />
            <Stat label={t('admin.mapData.calibrationPending')} value={pendingRuns.length} />
          </StatGrid>

          {runs.length === 0 ? (
            <EmptyState
              title={t('admin.mapData.calibrationEmptyTitle')}
              description={t('admin.mapData.calibrationEmpty')}
            />
          ) : (
            runs.map((run) => (
              <CollapsibleRow
                key={run.id}
                title={
                  <span className="text-sm font-medium">
                    {t(`admin.mapData.modeLabel.${run.mode}`)}
                    {' · '}
                    {t(`admin.mapData.bucket.${run.timeBucket}`)}
                  </span>
                }
                meta={
                  <span className="text-muted-foreground truncate text-xs">
                    {t('admin.mapData.calibrationError', {
                      before: run.maeBefore,
                      after: run.maeAfter,
                      count: run.sampleCount,
                    })}
                  </span>
                }
                badges={
                  <Badge variant={calibrationVariant(run.status)}>
                    {t(`admin.mapData.calibrationStatus.${run.status}`)}
                  </Badge>
                }
              >
                <dl className="text-muted-foreground grid gap-1 text-xs sm:grid-cols-2">
                  <div>
                    <dt className="inline">{t('admin.mapData.calibrationBefore')}: </dt>
                    <dd className="inline">{JSON.stringify(run.before)}</dd>
                  </div>
                  <div>
                    <dt className="inline">{t('admin.mapData.calibrationAfter')}: </dt>
                    <dd className="inline">{JSON.stringify(run.after)}</dd>
                  </div>
                  <div>
                    <dt className="inline">{t('admin.mapData.calibrationCreated')}: </dt>
                    <dd className="inline">{stamp(run.createdAt)}</dd>
                  </div>
                  {run.appliedAt !== null ? (
                    <div>
                      <dt className="inline">{t('admin.mapData.calibrationApplied')}: </dt>
                      <dd className="inline">{stamp(run.appliedAt)}</dd>
                    </div>
                  ) : null}
                </dl>
                {run.status === 'pending' ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      disabled={applyCalibration.isPending}
                      onClick={() => applyCalibration.mutate(run.id)}
                    >
                      {t('admin.mapData.calibrationApply')}
                    </Button>
                    <span className="text-muted-foreground text-xs">
                      {t('admin.mapData.calibrationApplyHint')}
                    </span>
                  </div>
                ) : null}
              </CollapsibleRow>
            ))
          )}
        </CardContent>
      </Card>

      {/* ---------------------------------------------------------------- */}
      {/* Lookup misses                                                     */}
      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3">
          <CardTitle>{t('admin.mapData.missesTitle')}</CardTitle>
          <Button
            variant="outline"
            size="sm"
            disabled={pruneMisses.isPending}
            onClick={() => pruneMisses.mutate(180)}
          >
            {pruneMisses.isPending
              ? t('admin.mapData.pruning')
              : t('admin.mapData.prune', { days: 180 })}
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          <p className="text-muted-foreground text-xs">{t('admin.mapData.missesHint')}</p>

          {misses.isPending ? <LoadingBlock label={t('common.loading')} /> : null}
          {misses.isError ? (
            <ErrorBlock
              title={t('admin.common.error')}
              retryLabel={t('admin.common.retry')}
              onRetry={() => void misses.refetch()}
            />
          ) : null}

          {misses.data && misses.data.items.length === 0 ? (
            <p className="text-muted-foreground">{t('admin.mapData.missesEmpty')}</p>
          ) : null}

          {misses.data && misses.data.items.length > 0 ? (
            <>
              <Pager
                page={misses.data.page}
                pageSize={misses.data.pageSize}
                total={misses.data.total}
                onPage={setPage}
              />
              {misses.data.items.map((item) => (
                <div
                  key={`${item.keyword}:${item.withNear}`}
                  className="flex flex-wrap items-center gap-2"
                >
                  <Badge variant="outline">{t('admin.mapData.missCount', { count: item.count })}</Badge>
                  <span className="text-sm font-medium">{item.keyword}</span>
                  {item.withNear ? (
                    <Badge variant="secondary">{t('admin.mapData.missWithNear')}</Badge>
                  ) : null}
                  <span className="text-muted-foreground text-xs">{stamp(item.lastAt)}</span>
                </div>
              ))}
              <Pager
                page={misses.data.page}
                pageSize={misses.data.pageSize}
                total={misses.data.total}
                onPage={setPage}
              />
            </>
          ) : null}
        </CardContent>
      </Card>

      {previewCalibration.data && previewCalibration.data.created.length === 0 ? (
        <Alert variant="info">
          <AlertTitle>{t('admin.mapData.calibrationNoneTitle')}</AlertTitle>
          <AlertDescription>{t('admin.mapData.calibrationNone')}</AlertDescription>
        </Alert>
      ) : null}
    </AdminPage>
  )
}
